/**
 * Extract plain text from Tiptap JSON format
 */
export function extractPlainTextFromTiptap(tiptapJson: string): string {
  if (!tiptapJson || tiptapJson.trim() === '') {
    return ''
  }

  try {
    const doc = JSON.parse(tiptapJson)

    if (!doc.content) {
      return ''
    }

    const extractTextFromNode = (node: any): string => {
      if (node.type === 'text') {
        return node.text || ''
      }

      if (node.type === 'mention') {
        return `@${node.attrs?.label || node.attrs?.id || ''}`
      }

      // Tables need explicit separators. Without them a row's cells concatenate
      // into an unsearchable run ("NameAgeAlice30"), and this string is what
      // feeds search and previews. Mirrors TiptapDialect.ExtractPlainText on the
      // backend: cells are tab-separated, rows newline-separated, and a cell is
      // always collapsed to a single line.
      if (node.type === 'tableCell' || node.type === 'tableHeader') {
        return (node.content ?? [])
          .map(extractTextFromNode)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim()
      }

      if (node.type === 'tableRow') {
        return (node.content ?? []).map(extractTextFromNode).join('\t')
      }

      if (node.type === 'table') {
        return (node.content ?? []).map(extractTextFromNode).join('\n')
      }

      if (Array.isArray(node.content)) {
        return node.content.map(extractTextFromNode).join('')
      }

      return ''
    }

    const textParts = doc.content
      .map((child: any) => {
        const text = extractTextFromNode(child)
        return text.trim()
      })
      .filter((text: string) => text.length > 0)

    return textParts.join('\n')
  } catch {
    return ''
  }
}

/**
 * Calculate the content length from Tiptap JSON
 */
export function calculateTiptapContentLength(tiptapJson: string): number {
  return extractPlainTextFromTiptap(tiptapJson).length
}
